import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import {
	InputOTP,
	InputOTPGroup,
	InputOTPSlot,
	InputOTPSeparator,
} from '@/components/ui/input-otp'
import { BrandLogo } from '@/components/branding/brand-logo'
import { useEffectiveBranding } from '@/hooks/use-effective-branding'

interface OtpVerificationProps {
	email: string
	onVerify: (code: string) => Promise<void>
	onBack: () => void
	loading?: boolean
	error?: string
}

export function OtpVerification({
	email,
	onVerify,
	onBack,
	loading = false,
	error,
}: OtpVerificationProps) {
	const { t } = useTranslation('auth')
	const [code, setCode] = useState('')

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault()
		if (code.length !== 6 || loading) return
		await onVerify(code)
	}

	const brand = useEffectiveBranding()

	return (
		<div className="flex min-h-screen items-center justify-center px-4 py-6 sm:p-8">
			<div className="w-full max-w-md space-y-6 sm:space-y-8">
				<div className="flex justify-center">
					<BrandLogo brand={brand} />
				</div>

				<div className="space-y-2 text-center">
					<h1 className="text-foreground text-3xl font-bold tracking-tight">
						{t('signUp.verification.title')}
					</h1>
					<p className="text-muted-foreground">
						{t('signUp.verification.subtitle')}{' '}
						<strong>{email}</strong>
					</p>
				</div>

				<form onSubmit={handleSubmit} className="space-y-6">
					<div className="flex justify-center">
						<InputOTP
							maxLength={6}
							value={code}
							onChange={setCode}
							disabled={loading}
							inputMode="numeric"
							autoComplete="one-time-code"
						>
							<InputOTPGroup>
								<InputOTPSlot index={0} />
								<InputOTPSlot index={1} />
								<InputOTPSlot index={2} />
							</InputOTPGroup>
							<InputOTPSeparator />
							<InputOTPGroup>
								<InputOTPSlot index={3} />
								<InputOTPSlot index={4} />
								<InputOTPSlot index={5} />
							</InputOTPGroup>
						</InputOTP>
					</div>

					<p className="text-muted-foreground text-center text-xs">
						{t('signUp.verification.codeHelper')}
					</p>

					{error && (
						<div className="bg-destructive/10 rounded-md p-3">
							<p className="text-destructive text-sm">{error}</p>
						</div>
					)}

					<Button
						type="submit"
						className="w-full"
						disabled={loading || code.length !== 6}
					>
						{loading ? (
							<span className="flex items-center gap-2">
								<span className="border-primary-foreground h-4 w-4 animate-spin rounded-full border-2 border-t-transparent" />
								{t('signUp.verification.verifying')}
							</span>
						) : (
							t('signUp.verification.verifyButton')
						)}
					</Button>

					<Button
						type="button"
						variant="ghost"
						className="w-full"
						onClick={onBack}
						disabled={loading}
					>
						{t('signUp.verification.backButton')}
					</Button>
				</form>
			</div>
		</div>
	)
}
